import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { PRODUCT_CLEAN } from "@/components/Hero";
import { CheckCircle2, Truck, ShieldCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OFFERS = [
  { qty: 1, price: 129, old: null, save: null, label: "قنينة واحدة" },
  { qty: 2, price: 229, old: 258, save: 29, label: "قنينتان", popular: true },
  { qty: 3, price: 319, old: 387, save: 68, label: "3 قنينات" },
];

export default function OrderForm() {
  const [form, setForm] = useState({ name: "", phone: "", city: "", address: "" });
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const total = OFFERS.find((o) => o.qty === qty).price;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("المرجو إدخال الاسم الكامل");
    if (!/^0[5-7]\d{8}$/.test(form.phone.replace(/\s/g, "")))
      return toast.error("المرجو إدخال رقم هاتف مغربي صحيح (مثال: 0689067964)");
    if (form.city.trim().length < 2) return toast.error("المرجو إدخال المدينة");
    if (form.address.trim().length < 5) return toast.error("المرجو إدخال العنوان الكامل");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders`, { ...form, quantity: qty });
      setOrder(data);
      if (typeof window.fbq === "function") {
        window.fbq("track", "Purchase", {
          value: data.total,
          currency: "MAD",
          content_name: "UCURE Spray Réparateur",
          num_items: data.quantity,
        });
      }
      if (window.ttq && typeof window.ttq.track === "function") {
        window.ttq.track("Purchase", {
          value: data.total,
          currency: "MAD",
          content_type: "product",
          contents: [
            {
              content_id: "ucure-spray-100ml",
              content_name: "UCURE Spray Réparateur",
              quantity: data.quantity,
              price: data.unit_price,
            },
          ],
        });
      }
      toast.success("تم استلام طلبك بنجاح");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg ? "تحقق من المعلومات المدخلة" : "حدث خطأ، حاول مجدداً");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#2E9E6B]/25 bg-white px-4 py-3.5 text-base font-medium text-[#0F1E19] outline-none transition-all duration-300 placeholder:text-[#566B63]/50 focus:border-[#1F7A52] focus:ring-2 focus:ring-[#2E9E6B]/20";

  return (
    <section id="order" className="mx-auto max-w-6xl scroll-mt-24 px-4 py-20 sm:px-6 lg:py-28">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <p className="text-sm font-bold tracking-[0.3em] text-[#2E9E6B]">اطلب الآن</p>
          <h2 className="font-display mt-3 text-2xl font-black leading-snug text-[#0F1E19] sm:text-3xl lg:text-4xl">
            الدفع عند الاستلام،
            <br />
            والتوصيل <span className="text-[#1F7A52]">مجاني</span> لباب دارك
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-[#566B63] sm:text-base">
            املأ الاستمارة وسنتصل بك لتأكيد الطلب. التوصيل متوفر لجميع المدن المغربية،
            وتدفع فقط عندما يصلك المنتج.
          </p>
          <div className="mt-8 flex items-center gap-5">
            <div className="w-28 shrink-0 overflow-hidden rounded-2xl border border-[#2E9E6B]/15 bg-white shadow-lg shadow-emerald-900/10 sm:w-36">
              <img src={PRODUCT_CLEAN} alt="بخاخ يوكيور بروفيشينال" className="h-full w-full object-cover" loading="lazy" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#0F1E19]">بخاخ يوكيور المرمّم · 100 مل</p>
              <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
                <span className="font-display text-3xl font-black text-[#1F7A52]" data-testid="order-unit-price">129 درهم</span>
                <span className="text-xs text-[#566B63] line-through">بدل 199 درهم</span>
              </p>
              <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-bold text-[#1F7A52]">
                <span className="flex items-center gap-1 rounded-full bg-[#E8F5EF] px-2.5 py-1"><Truck className="h-3 w-3" /> توصيل مجاني</span>
                <span className="flex items-center gap-1 rounded-full bg-[#E8F5EF] px-2.5 py-1"><ShieldCheck className="h-3 w-3" /> دفع عند الاستلام</span>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-3xl border border-[#2E9E6B]/20 bg-white p-7 shadow-xl shadow-emerald-900/10 sm:p-9" data-testid="cod-order-card">
            <AnimatePresence mode="wait">
              {order ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-6 text-center"
                  data-testid="order-success-panel"
                >
                  <CheckCircle2 className="mx-auto h-14 w-14 text-[#2E9E6B]" />
                  <h3 className="font-display mt-4 text-2xl font-black text-[#0F1E19]">شكراً {order.name.split(" ")[0]}!</h3>
                  <p className="mt-2 text-sm text-[#566B63]">تم تسجيل طلبك بنجاح. سنتصل بك قريباً على <span dir="ltr" className="font-bold text-[#0F1E19]">{order.phone}</span> لتأكيد التوصيل إلى {order.city}.</p>
                  <div className="mt-6 rounded-2xl bg-[#E8F5EF] p-5 text-sm">
                    <p className="flex justify-between"><span className="text-[#566B63]">رقم الطلب</span><span className="font-black text-[#1F7A52]" dir="ltr" data-testid="order-number">{order.order_number}</span></p>
                    <p className="mt-2 flex justify-between"><span className="text-[#566B63]">الكمية</span><span className="font-bold text-[#0F1E19]">{order.quantity}</span></p>
                    <p className="mt-2 flex justify-between"><span className="text-[#566B63]">التوصيل</span><span className="font-bold text-[#2E9E6B]">مجاني</span></p>
                    <p className="mt-3 flex justify-between border-t border-[#2E9E6B]/20 pt-3 text-base"><span className="font-bold text-[#0F1E19]">المجموع عند الاستلام</span><span className="font-black text-[#0F1E19]" data-testid="order-total">{order.total} درهم</span></p>
                  </div>
                  <button
                    onClick={() => { setOrder(null); setForm({ name: "", phone: "", city: "", address: "" }); setQty(1); }}
                    data-testid="order-again-button"
                    className="mt-6 text-sm font-bold text-[#1F7A52] underline-offset-4 transition-colors hover:text-[#0F1E19] hover:underline"
                  >
                    إرسال طلب آخر
                  </button>
                </motion.div>
              ) : (
                <motion.form key="form" exit={{ opacity: 0, scale: 0.97 }} onSubmit={submit} className="space-y-4">
                  <div>
                    <label htmlFor="cod-name" className="mb-1.5 block text-xs font-bold text-[#0F1E19]">الاسم الكامل</label>
                    <input id="cod-name" data-testid="cod-name-input" className={inputCls} placeholder="مثال: سارة العلوي" value={form.name} onChange={set("name")} />
                  </div>
                  <div>
                    <label htmlFor="cod-phone" className="mb-1.5 block text-xs font-bold text-[#0F1E19]">رقم الهاتف</label>
                    <input id="cod-phone" data-testid="cod-phone-input" className={inputCls} placeholder="06XXXXXXXX" dir="ltr" inputMode="tel" value={form.phone} onChange={set("phone")} />
                  </div>
                  <div>
                    <label htmlFor="cod-city" className="mb-1.5 block text-xs font-bold text-[#0F1E19]">المدينة</label>
                    <input id="cod-city" data-testid="cod-city-input" className={inputCls} placeholder="اكتب اسم مدينتك" value={form.city} onChange={set("city")} />
                  </div>
                  <div>
                    <label htmlFor="cod-address" className="mb-1.5 block text-xs font-bold text-[#0F1E19]">العنوان</label>
                    <input id="cod-address" data-testid="cod-address-input" className={inputCls} placeholder="الحي، الشارع، رقم العمارة أو المنزل…" value={form.address} onChange={set("address")} />
                  </div>
                  <div>
                    <span className="mb-2 block text-xs font-bold text-[#0F1E19]">اختر عرضك</span>
                    <div className="grid grid-cols-3 gap-2" data-testid="bundle-options">
                      {OFFERS.map((o) => (
                        <button
                          type="button"
                          key={o.qty}
                          data-testid={`bundle-option-${o.qty}`}
                          onClick={() => setQty(o.qty)}
                          className={`relative rounded-xl border-2 px-2 py-3 text-center transition-all duration-300 ${
                            qty === o.qty
                              ? "border-[#1F7A52] bg-[#E8F5EF] shadow-sm"
                              : "border-[#2E9E6B]/15 bg-white hover:border-[#2E9E6B]/40"
                          }`}
                        >
                          {o.popular && (
                            <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#D4AF37] px-2.5 py-0.5 text-[9px] font-black text-[#0F1E19]">
                              الأكثر طلباً
                            </span>
                          )}
                          <span className="block text-xs font-bold text-[#0F1E19]">{o.label}</span>
                          <span className="mt-0.5 block whitespace-nowrap text-base font-black text-[#1F7A52]">{o.price} درهم</span>
                          {o.old && <span className="block whitespace-nowrap text-[10px] text-[#566B63] line-through">بدل {o.old} درهم</span>}
                          {o.save && <span className="mt-0.5 block text-[10px] font-black text-[#E65A6B]">وفّر {o.save} درهم</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-[#0F1E19] px-5 py-4 text-[#FBF9F4]">
                    <span className="text-sm font-bold">المجموع <span className="text-[#9BD8BC]">(شحن مجاني)</span></span>
                    <span className="font-display text-xl font-black text-[#D4AF37]" data-testid="cod-total-price">{total} درهم</span>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="cod-submit-button"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1F7A52] py-4 text-base font-black text-[#FBF9F4] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0F1E19] hover:shadow-lg hover:shadow-emerald-900/25 disabled:opacity-60"
                  >
                    {loading && <Loader2 className="h-5 w-5 animate-spin" />}
                    تأكيد الطلب — الدفع عند الاستلام
                  </button>
                  <p className="text-center text-[11px] text-[#566B63]">بالضغط على تأكيد، أنت توافق على أن نتصل بك لترتيب التوصيل.</p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
